// @core/stores/payment/PaymentPaymentStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { paymentApi } from '@core/api';
import type {
  BatchGetEntityPaymentsRequest,
  BatchGetEntityPaymentsResponse,
  ChargeEntityRequest,
  Payment,
  PaymentLifecycleResponse,
  RefundEntityRequest,
} from '@core/types/payment';
import type { PageInfo } from '@core/types/pagination';

class PaymentPaymentStore {
  // List state
  items: Payment[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Payment | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Payment>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Payment | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Payment | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Batch get entity payments
   */
  async BatchGetEntityPayments(data: BatchGetEntityPaymentsRequest): Promise<BatchGetEntityPaymentsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await paymentApi.BatchGetEntityPayments(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Charge entity
   */
  async ChargeEntity(target_type: string, target_id: string, data: ChargeEntityRequest): Promise<PaymentLifecycleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await paymentApi.ChargeEntity(target_type, target_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get entity payment
   */
  async GetEntityPayment(target_type: string, target_id: string): Promise<Payment> {
    // Check cache first
    const cached = this.cache.get(target_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await paymentApi.GetEntityPayment(target_type, target_id);
      const item = response.payment;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Refund entity payment
   */
  async RefundEntity(target_type: string, target_id: string, data: RefundEntityRequest): Promise<PaymentLifecycleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await paymentApi.RefundEntity(target_type, target_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const paymentPaymentStore = new PaymentPaymentStore();
